import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import Button from '../ui/Button';

const MIN_ROWS = 3;
const MAX_ROWS = 20;
const ROW_HEIGHT = 24;

const FollowUpQueryBox = ({
	title,
	placeholder,
	submitText,
	actionLabel,
	onSubmit,
	showActionButton = true,
	onAction,
	size = 'medium',
}) => {
	const { t } = useTranslation();
	const [message, setMessage] = useState('');
	const [loading, setLoading] = useState(false);
	const textareaRef = useRef(null);

	const sizeClasses = {
		medium: 'w-full max-w-2xl',
	};

	const handleSubmit = async () => {
		if (!message.trim()) return;
		setLoading(true);

		try {
			await onSubmit(message);
			setMessage('');
		} catch (error) {
			console.error('Error in FollowUpQueryBox:', error);
		} finally {
			setLoading(false);
		}
	};

	const handleAction = () => {
		if (onAction) {
			onAction();
		} else {
			setMessage('');
		}
	};

	const handleKeyDown = (e) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			handleSubmit();
		}
	};

	// Auto-resize textarea function
	const autoResizeTextarea = () => {
		if (textareaRef.current) {
			textareaRef.current.style.height = 'auto';
			const scrollHeight = textareaRef.current.scrollHeight;
			const newRows = Math.min(Math.ceil(scrollHeight / ROW_HEIGHT), MAX_ROWS);
			const rows = Math.max(newRows, MIN_ROWS);
			textareaRef.current.style.height = `${rows * ROW_HEIGHT}px`;
		}
	};

	// Resize on component mount and when message changes
	useEffect(() => {
		autoResizeTextarea();
	}, [message]);

	const focusInput = () => {
		textareaRef.current.focus();
	};

	return (
		<div className='flex flex-col'>
			<div className='border-t border-tertiary'></div>
			<div className='border-l-4 border-cyan-200 pl-4 flex flex-col gap-4 pb-4'>
				<p className='text-on-secondary uppercase text-xs font-medium leading-[1.2] pt-4'>{title}</p>
				<div
					className={`relative border border-border-default focus-within:ring-1 focus-within:ring-border-active rounded-sm p-4 gap-3 flex flex-col justify-between ${sizeClasses[size]}`}
					onClick={focusInput}
				>
					<textarea
						className='resize-none focus-visible:outline-none w-full'
						placeholder={placeholder || t('query.follow_up_title', { app_name: t('general.app_name') })}
						value={message}
						onChange={(e) => setMessage(e.target.value)}
						onKeyDown={handleKeyDown}
						disabled={loading}
						ref={textareaRef}
						rows='3'
					/>
					<div className='flex justify-between items-end'>
						<div>
							{showActionButton && !loading && message.trim() !== '' && (
								<Button size='tiny' type='line' onClick={handleAction}>
									{actionLabel || t('query.action_button')}
								</Button>
							)}
						</div>
						<div className='flex flex-row items-end gap-6'>
							<Button
								variant='primary'
								size='medium'
								type='solid'
								disabled={message.trim() === ''}
								onClick={handleSubmit}
								loading={loading}
							>
								{submitText || t('query.submit_button')}
							</Button>
						</div>
					</div>
				</div>
			</div>
			<div className='border-t border-tertiary'></div>
		</div>
	);
};

FollowUpQueryBox.propTypes = {
	title: PropTypes.string,
	placeholder: PropTypes.string,
	submitText: PropTypes.string,
	actionLabel: PropTypes.string,
	onSubmit: PropTypes.func.isRequired,
	showActionButton: PropTypes.bool,
	onAction: PropTypes.func,
	size: PropTypes.oneOf(['medium']),
};

export default FollowUpQueryBox;
